/**
 * Newsletter signup: footer form and the browsing prompt.
 *
 * Both post straight to Klaviyo's public client subscription endpoint, which is built for
 * browser use with the public API key, so there is no server round trip and no secret in
 * the bundle.
 *
 * The list is single opt-in as of 2026-08-23, so a successful post subscribes immediately.
 */

const cfg = window.sgNewsletter;

/* -------------------------------------------------------------------------- */
/* Subscribe                                                                   */
/* -------------------------------------------------------------------------- */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function wire(form) {
	const email = form.querySelector('input[type="email"]');
	const honeypot = form.querySelector('input[name="company"]');
	const button = form.querySelector('button[type="submit"]');
	const scope = form.closest('[data-newsletter-scope]') || form.parentElement;
	const status = scope?.querySelector('.sg-newsletter-status');

	const say = (text, state) => {
		if (!status) return;
		status.textContent = text;
		status.dataset.state = state;
	};

	form.addEventListener('submit', async (event) => {
		event.preventDefault();

		// Bots fill the hidden field. Pretend it worked and drop it on the floor.
		if (honeypot && honeypot.value !== '') {
			say(cfg.messages.success, 'success');
			return;
		}

		const value = (email?.value || '').trim();
		if (!value || !EMAIL.test(value)) {
			say(cfg.messages.invalid, 'error');
			email?.focus();
			return;
		}

		button.disabled = true;
		say(cfg.messages.working, 'working');

		const source = form.dataset.source || cfg.source;

		try {
			const response = await fetch(
				`${cfg.endpoint}?company_id=${encodeURIComponent(cfg.public_key)}`,
				{
					method: 'POST',
					headers: {
						revision: cfg.revision,
						'Content-Type': 'application/vnd.api+json',
					},
					body: JSON.stringify({
						data: {
							type: 'subscription',
							attributes: {
								custom_source: source,
								profile: {
									data: {
										type: 'profile',
										attributes: {
											email: value,
											subscriptions: {
												email: { marketing: { consent: 'SUBSCRIBED' } },
											},
										},
									},
								},
							},
							relationships: {
								list: { data: { type: 'list', id: cfg.list_id } },
							},
						},
					}),
				}
			);

			// The endpoint answers 202 Accepted with an empty body on success.
			if (response.ok) {
				say(cfg.messages.success, 'success');
				form.reset();
				email.disabled = true;
				remember(KEY_DONE);
				window.setTimeout(hidePrompt, 2500);
				return;
			}

			say(cfg.messages.error, 'error');
			button.disabled = false;
		} catch (error) {
			say(cfg.messages.error, 'error');
			button.disabled = false;
		}
	});
}

/* -------------------------------------------------------------------------- */
/* The browsing prompt                                                         */
/* -------------------------------------------------------------------------- */

const KEY_DONE = 'sg_newsletter_done';
const KEY_SEEN = 'sg_newsletter_dismissed';
const DAYS_DONE = 3650;
const DAYS_SEEN = 90;
const SCROLL_TRIGGER = 0.6;
const IDLE_MS = 45000;

function remember(key) {
	try {
		window.localStorage.setItem(key, String(Date.now()));
	} catch (error) {
		/* private mode, storage disabled. The prompt simply reappears next visit. */
	}
}

function within(key, days) {
	try {
		const stamp = Number(window.localStorage.getItem(key));
		if (!stamp) return false;
		return Date.now() - stamp < days * 86400000;
	} catch (error) {
		return false;
	}
}

const prompt = document.querySelector('.sg-prompt');

function hidePrompt() {
	if (!prompt) return;
	prompt.classList.remove('is-open');
	window.setTimeout(() => {
		prompt.hidden = true;
	}, 300);
}

function showPrompt() {
	if (!prompt || prompt.hidden === false) return;
	prompt.hidden = false;
	// Force a reflow so the transition has a starting point, then reveal. Deliberately
	// not requestAnimationFrame: it does not fire in a background tab, which would leave
	// the prompt stuck at opacity 0 forever once the idle timer had armed it.
	void prompt.offsetHeight;
	prompt.classList.add('is-open');
}

function armPrompt() {
	if (!prompt) return;
	if (within(KEY_DONE, DAYS_DONE) || within(KEY_SEEN, DAYS_SEEN)) return;

	let fired = false;
	const fire = () => {
		if (fired) return;
		// Never stack on top of a cookie banner. Both sit at the bottom of the screen,
		// and the banner has to be answered first. The banner markup is not rendered
		// today (see inc/integrations/consent-manager.php), but the CSS and JS for it
		// exist, so this collision is one deploy away.
		if (document.getElementById('sg-consent-banner')) return;
		fired = true;
		showPrompt();
	};

	const isMobile = window.matchMedia('(max-width: 767px)').matches;

	if (isMobile) {
		// Scroll depth. A bottom bar, never a full screen overlay: Google demotes
		// intrusive interstitials on mobile and organic search matters here.
		const onScroll = () => {
			const seen = window.scrollY + window.innerHeight;
			const height = document.documentElement.scrollHeight;
			if (height > 0 && seen / height >= SCROLL_TRIGGER) fire();
		};
		window.addEventListener('scroll', onScroll, { passive: true });
	} else {
		// Exit intent. Only counts when the pointer leaves past the top of the window.
		document.addEventListener('mouseout', (event) => {
			if (!event.relatedTarget && event.clientY <= 0) fire();
		});
	}

	// Fallback for people who neither scroll nor leave.
	window.setTimeout(fire, IDLE_MS);

	prompt.querySelector('.sg-prompt-close')?.addEventListener('click', () => {
		remember(KEY_SEEN);
		hidePrompt();
	});

	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape' && !prompt.hidden) {
			remember(KEY_SEEN);
			hidePrompt();
		}
	});

	// Someone who uses the footer form should not then be nagged by the prompt.
	document.querySelector('.sg-newsletter:not(.sg-prompt-form)')?.addEventListener('submit', () => {
		fired = true;
	});
}

/* -------------------------------------------------------------------------- */

if (cfg && cfg.public_key && cfg.list_id) {
	document.querySelectorAll('.sg-newsletter').forEach(wire);

	if (prompt) {
		prompt.querySelector('.sg-prompt-form')?.setAttribute(
			'data-source',
			window.matchMedia('(max-width: 767px)').matches
				? 'Pop-up mobil, sansegynge.dk'
				: 'Pop-up desktop, sansegynge.dk'
		);
		armPrompt();
	}
}
